import React, { useEffect, useRef } from "react";
import { CounterValueAndLabel, CounterSettings } from "@/shared/types";
import { counterUp } from "./counterUp";

interface CounterProps {
  counterSettings?: CounterSettings;
}

function Counter({ counterSettings }: CounterProps) {
  const targetRef = useRef<HTMLDivElement>(null);
  const counterValueSettings =
    counterSettings?.counterValue as CounterValueAndLabel;
  const counterLabelSettings =
    counterSettings?.counterLabel as CounterValueAndLabel;
  const counterSpeed = counterSettings?.animationSpeed!;
  const counterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Start the counter animation when the target is visible
          if (counterRef.current) {
            const duration = 2000 - counterSpeed * 12;
            counterUp(counterRef.current, {
              duration: counterSpeed === 0 ? 0 : duration,
            });
          }
          // Unobserve the target once animation is started
          observer.unobserve(entry.target);
        }
      });
    });

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    // Clean up the observer
    return () => {
      observer.disconnect();
    };
  }, [counterSpeed]);

  const smallScale = 0.9;
  const mediumScale = 0.85;
  const smallestScreen = 375;
  const largestScreen = 1440;
  const range = largestScreen - smallestScreen;
  const valueFontSize = counterValueSettings?.size || 48;
  const labelFontSize = counterLabelSettings.size || 16;
  const minValueFontSize = valueFontSize * mediumScale;
  const minLabelFontSize = labelFontSize * smallScale;
  const valueFontSizeCalc = `clamp(${minValueFontSize}px, calc(${minValueFontSize}px + (${valueFontSize} - ${minValueFontSize}) * ((100vw - ${smallestScreen}px) / (${range}))), ${valueFontSize}px)`;
  const labelFontSizeCalc = `clamp(${minLabelFontSize}px, calc(${minLabelFontSize}px + (${labelFontSize} - ${minLabelFontSize}) * ((100vw - ${smallestScreen}px) / (${range}))), ${labelFontSize}px)`;

  return (
    <div ref={targetRef} id="general-text">
      <div
        style={
          {
            "--counter-value-font-size": valueFontSizeCalc,
            fontSize: `var(--counter-value-font-size)`,
            lineHeight: "normal",
            color: counterValueSettings?.color?.value,
          } as React.CSSProperties
        }
      >
        <div ref={counterRef} data-counter="counterup">
          {counterValueSettings.value || "0"}
        </div>
      </div>
      {counterSettings?.LabelEnabled && (
        <div
          style={
            {
              "--counter-label-font-size": labelFontSizeCalc,
              fontSize: `var(--counter-label-font-size)`,
              lineHeight: "normal",
              color: counterLabelSettings.color?.value,
            } as React.CSSProperties
          }
        >
          {counterLabelSettings.value}
        </div>
      )}
    </div>
  );
}

export default Counter;
